import React, { useCallback, useMemo } from 'react';
import { Modal, Button, Empty, Spin, Typography, message, Space } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { SourceEpisode } from '../../types';
import { sourceEpisodesApi } from '../../services/api/sourceEpisodes';
import { useApi } from '../../hooks';

const { Paragraph, Text } = Typography;

interface EpisodeContentModalProps {
  visible: boolean;
  episode: SourceEpisode;
  onClose: () => void;
}

const EpisodeContentModal: React.FC<EpisodeContentModalProps> = ({
  visible,
  episode,
  onClose,
}) => {
  const {
    data: panelsData,
    loading: panelsLoading,
  } = useApi(
    useCallback(() => sourceEpisodesApi.getEpisodePanels(episode.id), [episode.id]),
    { immediate: visible }
  );

  const panels = panelsData?.data || [];

  const fullContent = useMemo(() => {
    return panels
      .map(panel => panel.localizedCaption || panel.caption || '')
      .filter(text => text.trim().length > 0)
      .join('\n\n');
  }, [panels]);

  const handleCopy = () => {
    if (!fullContent) {
      message.warning('No content to copy');
      return;
    }

    navigator.clipboard.writeText(fullContent)
      .then(() => {
        message.success('Copied to clipboard!');
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
        message.error('Failed to copy to clipboard');
      });
  };

  return (
    <Modal
      title={`Episode Content: ${episode.name}`}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="copy" type="primary" icon={<CopyOutlined />} onClick={handleCopy} disabled={!fullContent}>
          Copy Content
        </Button>,
        <Button key="close" onClick={onClose}>
          Close
        </Button>
      ]}
      width={700}
      centered
      destroyOnClose
    >
      <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '10px' }}>
        {panelsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin tip="Loading content..." />
          </div>
        ) : panels.length === 0 ? (
          <Empty description="No panels in this episode" />
        ) : !fullContent ? (
          <Empty description="No text content found in panels" />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">
                Total {panels.length} panels. Showing localized captions (fallback to original if not localized).
              </Text>
            </div>
            <Paragraph style={{ whiteSpace: 'pre-wrap', fontSize: '16px', lineHeight: '1.6' }}>
              {fullContent}
            </Paragraph>
          </Space>
        )}
      </div>
    </Modal>
  );
};

export default EpisodeContentModal;
